import { faPause, faPlay, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { mergeRefs } from '../../hooks'
import { AnimatePresence, motion } from 'framer-motion'
import React, { forwardRef, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { twMerge } from 'tailwind-merge'
import { Icon } from '../Icon/Icon'
import { getRatio } from './media.helpers'

export type VideoProps = {
  src: string
  alt: string
  height?: string | number
  overlay?: boolean
  blur?: boolean
  captionsSrc?: string
  className?: string
  placeholderClassName?: string
  autoPlay?: boolean
  aspectRatio?: '1:1' | '9:16' | '16:9' | '5:4' | '4:5'
  fit?: 'object-cover' | 'object-contain'
  children?: React.ReactNode
}

export const Video = forwardRef(
  (
    { src, aspectRatio, placeholderClassName, ...props }: VideoProps,
    videoRef,
  ) => {
    const [loaded, setLoaded] = useState(false)
    const [error, setError] = useState(false)
    const [playing, setPlaying] = useState(false)

    const { ref, inView } = useInView({
      threshold: 0,
      delay: 300,
      triggerOnce: true,
    })
    const internalVideoRef = useRef<HTMLVideoElement>(null)

    // prettier-ignore
    const overlayClasses = props.overlay && loaded ? `bg-opacity-75 bg-blend-overlay bg-base-800` : ''
    const blurClasses = props.blur && loaded ? 'backdop-blur-sm' : ''

    const pauseVideo = () => {
      if (props.autoPlay) return
      if (!internalVideoRef.current) return
      internalVideoRef.current.pause()
      setPlaying(false)
    }

    const playVideo = () => {
      if (props.autoPlay) return
      if (!internalVideoRef.current) return
      internalVideoRef.current.play()
      setPlaying(true)
    }

    return (
      <div
        ref={ref}
        // prettier-ignore
        className={`relative isolate overflow-hidden ${getRatio(aspectRatio)} ${props.className}`}
        onMouseOver={playVideo}
        onMouseLeave={pauseVideo}>
        {/* Skeleton Loader */}
        <AnimatePresence>
          {!loaded && (
            <motion.div
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 1 }}
              className={twMerge(
                `absolute inset-0 flex justify-center items-center z-20 bg-base-200 animate-pulse`,
                placeholderClassName,
              )}>
              <Icon
                icon={faSpinner}
                size='lg'
                className='text-base-0/30 animate-spin'
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Actual Video */}
        <AnimatePresence>
          {!error && (
            <motion.video
              {...props}
              aria-label={props.alt} // video elements do not support alt text, but have an aria label attribute that works similarly
              disableRemotePlayback={false}
              ref={mergeRefs(videoRef, internalVideoRef)}
              src={src}
              style={{ opacity: inView ? 1 : 0 }}
              muted
              autoPlay={props.autoPlay}
              loop
              playsInline
              onLoadedData={() => setLoaded(true)}
              // the native error event given from onError tells us literally nothing, which is why we only have a boolean state
              onError={() => setError(true)}
              className={`absolute z-10 inset-0 w-full h-full ${
                props.fit || 'object-cover'
              }`}>
              {props.captionsSrc && (
                <track
                  kind='captions'
                  src={props.captionsSrc}
                  srcLang='en'
                  label='English'
                />
              )}

              <source src={src} type='video/mov' />
              <source src={src} type='video/mp4' />
              <source src={src} type='video/quicktime' />
            </motion.video>
          )}
        </AnimatePresence>

        {error && (
          <div
            className={`flex absolute z-10 top-0 overflow-hidden object-cover items-center justify-center bg-slate-200`}>
            <p className='font-medium text-xs text-center'>
              video failed to load
            </p>
          </div>
        )}

        {!props.autoPlay && !error && (
          <button
            onClick={() => (playing ? pauseVideo() : playVideo())}
            type='button'>
            {internalVideoRef.current && (
              <FontAwesomeIcon
                icon={playing ? faPause : faPlay}
                className='absolute z-40 inset-0 hover:text-indigo-200 hover:border-indigo-200 bg-base-800 bg-opacity-75 right-0 m-2 text-white h-4! w-4! p-2 border border-white rounded-full flex justify-center items-center'
              />
            )}
          </button>
        )}

        {/* Children are placed on top of the photo */}
        <div
          className={`z-30 overflow-hidden absolute inset-0 ${overlayClasses} ${blurClasses}`}>
          {props.children}
        </div>
      </div>
    )
  },
)
